import React, { useMemo } from 'react'
import { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { connect } from 'react-redux'
import { useRouter } from 'next/router'
import DatePicker, { registerLocale } from 'react-datepicker'
import {
  FormControl,
  Tooltip,
  useMediaQuery,
  useTheme,
  withStyles,
} from '@material-ui/core'
import DateRangeIcon from '@material-ui/icons/DateRange'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import Skeleton from '@material-ui/lab/Skeleton'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import Button from '../Button/Button'
import ShippingInfos from '../ShippingInfos/ShippingInfos'
import useStyles from './DateRangeForm.style'
import Loader from '../Loader/Loader'
import { scrollToDiv } from '../../utils/utils'
import {
  customWarehouse,
  enableHalfDay,
  isSubscription,
  store,
} from '../../utils/constants'
import CMSText, { isPrismicText, prismicText } from '../Global/CMSText'
import {
  getPromotions,
  setCurrentPromotion,
} from '../../redux/actions/promotions'
import { ERequestUrl } from '../../redux/helpers/requests'
import { isFlex } from '../../utils/constants'
import {
  getMinimumStartDate,
  isNonBusinessDay,
  isStartDateAlignedWithSylius,
} from '../../utils/businessDays'

//Add locale translations of date picker
import fr from 'date-fns/locale/fr'
import de from 'date-fns/locale/de'
import es from 'date-fns/locale/es'
import nl from 'date-fns/locale/nl'
import { useCart } from '../../hooks/useCart'
import { GetMapDispatchTypes } from '../../interfaces/common/redux'
import { RootState } from '../../redux/reducers/rootReducer'
import { ReactSVG } from 'react-svg'
import { proxySvgUrl } from '@/utils/proxySvgUrl'
import { PrismicGeneralData } from '@/interfaces/common/prismicGeneralConfig'

registerLocale('fr', fr)
registerLocale('de', de)
registerLocale('es', es)
registerLocale('nl', nl)

function getMajeRentalLastDay() {
  return store === 'maje' ? dayjs('2026-06-30', 'YYYY-MM-DD') : null
}

function getRentalEndMaxDate(
  startLeasingDate: string | undefined,
  maximumLeasingDays: number | undefined
) {
  const fromParams = dayjs(startLeasingDate, 'DD/MM/YYYY').add(
    maximumLeasingDays - 1,
    'day'
  )
  const majeLast = getMajeRentalLastDay()
  if (majeLast && fromParams.isAfter(majeLast)) {
    return majeLast.toDate()
  }
  return fromParams.toDate()
}

type Tprops = {
  isLanding?: boolean
  text?: PrismicGeneralData
  textFunnel?: any
  t: any
  theme?: any
  setCurrentPromotion: any
  scrollTarget?: string
} & ReturnType<typeof mapStateToProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps>

const MainTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    fontSize: 11,
    padding: theme.spacing(1),
  },
  arrow: {
    color: theme.palette.secondary.main,
  },
}))(Tooltip)

const mapStateToProps = (state: RootState) => ({
  cart: state.cart,
  promotions: state.promotions,
  parameters: state.parameters,
  stores: state.stores,
})

const mapDispatchToProps = {
  getPromotions: getPromotions,
  setCurrentPromotion: setCurrentPromotion,
}

const DateRange = ({
  isLanding,
  cart,
  t,
  parameters,
  setCurrentPromotion,
  getPromotions,
  text,
  textFunnel,
  scrollTarget,
  stores,
  promotions,
}: Tprops) => {
  const { deleteCart, createCart, patchDatesCart } = useCart()
  const classes = useStyles()
  const theme = useTheme()
  const router = useRouter()

  const [publicHolidays, setPublicHolidays] = useState<Set<string>>(new Set())
  const [minDateRange, setMinDateRange] = useState(2)
  const [minStartDate, setMinStartDate] = useState(
    dayjs().add(7, 'day').toDate()
  )
  const [minEndDate, setMinEndDate] = useState(
    dayjs(minStartDate).add(1, 'day').toDate()
  )

  const [excludedDatesStart, setExcludedDatesStart] = useState([])
  const [excludedDatesEnd, setExcludedDatesEnd] = useState([])
  const [daysOff, setDaysOff] = useState([])
  const startRef = useRef<DatePicker>()
  const endRef = useRef<DatePicker>()
  const [hasReduction, setHasReduction] = useState(false)
  const [reduction, setReduction] = useState(0)
  const [displayTooltip, setDisplayTooltip] = useState(true)
  const [calendarFade, setCalendarFade] = useState(false)
  const [dateLoading, setDateLoading] = useState(false)
  const [dayDuration, setDayDuration] = useState('day')

  const calendarIconPrismic = text?.datepickerbutton
  const isCalendarIconDefined =
    calendarIconPrismic?.url &&
    calendarIconPrismic?.url?.length > 0 &&
    calendarIconPrismic?.url.split('.').pop() === 'svg'
  const { trackEvent } = useMatomo()
  const dayDurationValues = [
    {
      value: 'day',
      text: text?.fullday,
      defaultText: t('texts:general:fullDay'),
    },
    {
      value: 'morning',
      text: text?.morning,
      defaultText: t('texts:general:morning'),
    },
    {
      value: 'afternoon',
      text: text?.afternoon,
      defaultText: t('texts:general:afternoon'),
    },
    {
      value: 'evening',
      text: text?.evening,
      defaultText: t('texts:general:evening'),
    },
  ]

  const CalendarIcon = isCalendarIconDefined ? (
    <ReactSVG
      src={proxySvgUrl(calendarIconPrismic.url)}
      className={clsx(
        'daterange__icon',
        'daterange__icon--calendar',
        classes.btnIcon,
        classes.btnIconSvg
      )}
      wrapper="span"
    />
  ) : (
    <DateRangeIcon
      className={clsx(
        'daterange__icon',
        'daterange__icon--calendar',
        classes.when__icon
      )}
    />
  )

  const currentLocale = useMemo(() => {
    let lang = router.locale
    if (router.locale === 'en') {
      const en_Region = process.env.NEXT_PUBLIC_EN_REGION
      lang = `${router.locale}-${
        en_Region && en_Region !== 'GB' ? en_Region : 'GB'
      }`
    }

    return lang ?? 'fr'
  }, [router.locale])

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.down('md'))
  const isEnableChooseStartingDate = parameters?.items?.enableChooseStartingDate

  let warehouseId = stores?.selectedStore?.warehouseId
  if (!warehouseId && customWarehouse && !isNaN(+customWarehouse)) {
    warehouseId = +customWarehouse
  }

  const minimumStartingDays = stores?.selectedStore?.minimumStartingDays
    ? stores.selectedStore.minimumStartingDays
    : parameters?.items?.minimumStartingDays

  const minimumLeasingDays = stores?.selectedStore?.minimumLeasingDays
    ? stores.selectedStore?.minimumLeasingDays
    : parameters?.items?.minimumLeasingDays
  const isSaturdayABusinessDay = parameters?.items?.isSaturdayABusinessDay
  const disableSundayFromCms = !!text?.disable_sunday_on_date_picker

  const handleDateChangeRaw = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    event.preventDefault()
  }

  useEffect(() => {
    // find currently selected duration from cart
    if (cart?.item?.oneDayDuration) {
      setDayDuration(
        dayDurationValues.find(
          (duration) => duration.value === cart?.item?.oneDayDuration
        ).value
      )
    }
  }, [])

  useEffect(() => {
    if (!isNaN(minimumStartingDays)) {
      setMinStartDate(
        getMinimumStartDate(
          minimumStartingDays,
          isSaturdayABusinessDay,
          publicHolidays,
          disableSundayFromCms
        ).toDate()
      )
    }
    if (!isNaN(minimumLeasingDays)) {
      setMinDateRange(minimumLeasingDays)
      setMinEndDate(
        getMinimumStartDate(
          minimumStartingDays,
          isSaturdayABusinessDay,
          publicHolidays,
          disableSundayFromCms
        )
          .add(minimumLeasingDays - 1, 'day')
          .toDate()
      )
    }
  }, [
    disableSundayFromCms,
    isSaturdayABusinessDay,
    minimumStartingDays,
    minimumLeasingDays,
    publicHolidays,
  ])

  useEffect(() => {
    calendarFade
      ? document?.body.classList.add('react-datepicker--open')
      : document?.body.classList.remove('react-datepicker--open')
  }, [calendarFade])

  useEffect(() => {
    let isMounted = true

    const daysOffArr = [...daysOff]
    let blockedStart = text?.blocked_dates_start as any
    let blockedEnd = text?.blocked_dates_end as any
    if (
      blockedStart !== null &&
      typeof blockedStart !== 'undefined' &&
      blockedEnd !== null &&
      typeof blockedEnd !== 'undefined'
    ) {
      const excludedDates = []
      blockedStart = new Date(blockedStart)
      blockedEnd = new Date(blockedEnd)
      while (blockedStart <= blockedEnd) {
        excludedDates.push(new Date(blockedStart))
        blockedStart.setDate(blockedStart.getDate() + 1)
      }
      excludedDates.map((dates) => {
        daysOffArr.push(dayjs(dates).toDate())
      })

      if (isMounted) {
        setDaysOff(excludedDates)
        if (excludedDatesStart.length === 0) {
          setExcludedDatesStart(excludedDates)
        }
        if (excludedDatesEnd.length === 0) {
          setExcludedDatesEnd(excludedDates)
        }
      }
    }

    const fetchDaysOff = async () => {
      try {
        let data = {}
        const responseNow = await fetch(
          `${ERequestUrl.apiGouvFr}/${dayjs().year()}.json`
        )
        const responsePlusOne = await fetch(
          `${ERequestUrl.apiGouvFr}/${dayjs().add(1, 'y').year()}.json`
        )
        const dataNow = await responseNow.json()
        const dataPlusOne = await responsePlusOne.json()

        data = { ...dataNow, ...dataPlusOne }
        setPublicHolidays(new Set<string>(Object.keys(data)))
        for (const [key] of Object.entries(data)) {
          daysOffArr.push(dayjs(key).toDate())
        }

        if (isMounted) {
          setDaysOff(daysOffArr)
          if (excludedDatesStart.length === 0) {
            setExcludedDatesStart(daysOffArr)
          }
          if (excludedDatesEnd.length === 0) {
            setExcludedDatesEnd(daysOffArr)
          }
        }
      } catch (error) {
        console.error('Error fetching days off:', error)
      }
    }

    if (process.env.DISABLE_DAYSOFF === 'false') {
      fetchDaysOff()
    }

    return () => {
      isMounted = false
    }
  }, [excludedDatesStart, excludedDatesEnd])

  const filterDate = (date) => {
    const disabledByBusinessRules = isNonBusinessDay(
      dayjs(date),
      isSaturdayABusinessDay,
      publicHolidays,
      disableSundayFromCms
    )
    const majeLast = getMajeRentalLastDay()
    if (majeLast && dayjs(date).isAfter(majeLast, 'day')) {
      return false
    }
    const weekDay = dayjs(date).day()
    const configWeekDayAndNumber = {
      0: !!text?.disable_sunday_on_date_picker,
      1: !!text?.disable_monday_on_date_picker,
      6: !!text?.disable_saturday_on_date_picker,
    }

    if (disabledByBusinessRules || configWeekDayAndNumber[weekDay]) {
      return false
    }

    return true
  }

  const handleOpen = () => {
    setCalendarFade(true)
    hasReduction && setDisplayTooltip(false)
  }

  const handleClose = () => {
    setCalendarFade(false)
    hasReduction && setDisplayTooltip(true)
  }

  const handleSubscriptionDayChange = async (date) => {
    setDateLoading(true)
    trackEvent({
      category: `DatePicker`,
      action: `User select ${dayjs(date).format('DD/MM/YYYY')} as StartDate`,
    })

    try {
      if (cart && cart.item) {
        if (cart?.item?.checkoutState === 'cart') {
          const subscriptionDate = dayjs(date).format('DD/MM/YYYY')
          await patchDatesCart({ startRentingDate: subscriptionDate })
        } else {
          // Destroy cart
          await deleteCart(true)
          await createCart(dayjs(date), null, warehouseId)
        }
      }
    } catch (error) {
      console.error('Error in handleSubscriptionDayChange:', error)
    } finally {
      setDateLoading(false)
    }
  }

  const handleStartDayChange = async (date) => {
    setDateLoading(true)
    trackEvent({
      category: `DatePicker`,
      action: `User select ${dayjs(date).format('DD/MM/YYYY')} as StartDate`,
    })

    try {
      if (cart && cart.item) {
        // If endDate < startdate selected by customer
        const endDatePlusOne = dayjs(date).add(minDateRange - 1, 'day')
        if (dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY') <= dayjs(date)) {
          // Sylius don't accept to patch the dates if cart isn't in cart stage. That mean that if the customer went to shipping et came back to shop to change dates, we have to destroy basket et create a new one
          if (cart?.item?.checkoutState === 'cart') {
            // Keep cart and put dates with selected date as startDate and slected date + 1 day as endDate
            const startDate = dayjs(date).format('DD/MM/YYYY')
            const endDate = dayjs(endDatePlusOne).format('DD/MM/YYYY')
            await patchDatesCart({
              startRentingDate: startDate,
              endRentingDate: endDate,
              oneDayDuration: startDate === endDate ? dayDuration : null,
            })
          } else {
            // Destroy cart
            await deleteCart(true)
            await createCart(dayjs(date), dayjs(endDatePlusOne), warehouseId)
          }
        } else {
          // endDate > startDate selected by customer
          const dateRange = dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').diff(
            dayjs(date),
            'day'
          )
          if (dateRange > minimumLeasingDays - 1) {
            if (dateRange <= parameters?.items?.maximumLeasingDays) {
              const startDate = dayjs(date).format('DD/MM/YYYY')
              const endDate = cart.item.endLeasingDate
              await patchDatesCart({
                startRentingDate: startDate,
                endRentingDate: endDate,
                oneDayDuration: startDate === endDate ? dayDuration : null,
              })
            } else {
              const startDate = dayjs(date).format('DD/MM/YYYY')
              const endDate = dayjs(date)
                .add(parameters?.items?.maximumLeasingDays - 1, 'day')
                .format('DD/MM/YYYY')
              await patchDatesCart({
                startRentingDate: startDate,
                endRentingDate: endDate,
                oneDayDuration: startDate === endDate ? dayDuration : null,
              })
            }
          } else {
            const startDate = dayjs(date).format('DD/MM/YYYY')
            const endDate = dayjs(endDatePlusOne).format('DD/MM/YYYY')
            await patchDatesCart({
              startRentingDate: dayjs(date).format('DD/MM/YYYY'),
              endRentingDate: dayjs(endDatePlusOne).format('DD/MM/YYYY'),
              oneDayDuration: startDate === endDate ? dayDuration : null,
            })
          }
          if (cart?.item?.checkoutState === 'cart') {
            // endDate > startDate selected by customer
            // put dates with selected date as startDate and current endDate
          } else {
            // cart state is too advanced, we have to destroy cart and create it back
            await deleteCart(true)
            await createCart(
              dayjs(date),
              dayjs(cart.item.endLeasingDate),
              warehouseId
            )
          }
        }
      } else if (isStartDateAlignedWithSylius()) {
        // No cart (its automatic creation was refused): the picked date must
        // still give the customer one, otherwise the picker does nothing.
        await createCart(
          dayjs(date),
          dayjs(date).add(minDateRange - 1, 'day'),
          warehouseId
        )
      }

      const excludeDateArray = [...daysOff]
      for (let i = 0; i < minDateRange - 1; i++) {
        excludeDateArray.push(dayjs(date).add(i, 'day').toDate())
      }
      setExcludedDatesEnd(excludeDateArray)
    } catch (error) {
      console.error('Error in handleStartDayChange:', error)
    } finally {
      setDateLoading(false)
    }
  }

  const handleEndDayChange = async (date) => {
    setDateLoading(true)
    trackEvent({
      category: `DatePicker`,
      action: `User select ${dayjs(date).format('DD/MM/YYYY')} as EndDate`,
    })

    try {
      if (cart && cart.item) {
        // If selected endDate is strictly after the startDate
        if (
          dayjs(date).diff(
            dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY'),
            'd'
          ) > 0
        ) {
          if (cart?.item?.checkoutState === 'cart') {
            // If checkoutState is ok, we put dates with current startDate and selected endDate
            const startDate = dayjs(
              cart.item.startLeasingDate,
              'DD/MM/YYYY'
            ).format('DD/MM/YYYY')
            const endDate = dayjs(date).format('DD/MM/YYYY')
            // If checkoutState is ok, we put dates with current startDate and selected endDate
            await patchDatesCart({
              startRentingDate: startDate,
              endRentingDate: endDate,
              oneDayDuration: startDate === endDate ? dayDuration : null,
            })
          } else {
            // If cart state is too advanced, we create a new cart
            await deleteCart(true)
            await createCart(
              dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY'),
              dayjs(date),
              warehouseId
            )
          }
        } else {
          // If selected endDate is before current startDate
          const startDateMinusOne = dayjs(date).subtract(
            minDateRange - 1,
            'day'
          )
          if (cart?.item?.checkoutState === 'cart') {
            // put dates with startDate as selected date - 1 day and endDate as selected date
            const startDate = dayjs(startDateMinusOne).format('DD/MM/YYYY')
            const endDate = dayjs(date).format('DD/MM/YYYY')
            await patchDatesCart({
              startRentingDate: startDate,
              endRentingDate: endDate,
              oneDayDuration: startDate === endDate ? dayDuration : null,
            })
          } else {
            await deleteCart(true)
            await createCart(dayjs(startDateMinusOne), dayjs(date), warehouseId)
          }
        }
        const startDateMinusOne = dayjs(date).subtract(minDateRange - 1, 'day')
        const excludeDateArray = [...daysOff]
        for (let i = 0; i < minDateRange - 2; i++) {
          excludeDateArray.push(dayjs(startDateMinusOne).add(i, 'day').toDate())
        }
        setExcludedDatesEnd(excludeDateArray)
      }
    } catch (error) {
      console.error('Error in handleEndDayChange:', error)
    } finally {
      setDateLoading(false)
    }
  }

  const onSubmit = (e) => {
    e.preventDefault()
    router.pathname === '/' ? router.push('/shop') : scrollToDiv(scrollTarget)
    if (cart?.item?.endLeasingDate && cart?.item?.startLeasingDate) {
      const rentingPeriod =
        dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').diff(
          dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY'),
          'd'
        ) + 1
      trackEvent({
        category: 'DatePicker',
        href: '/shop',
        action: `User clicked on start button and want to rent for ${rentingPeriod} days`,
      })
    }
  }

  useEffect(() => {
    let isMounted = true

    if (cart?.item) {
      let promotionPercentage = 0
      const start = dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY').format()
      const end = dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').format()
      const range = dayjs(end).diff(dayjs(start), 'day') + 1
      const calculPromotion = async () => {
        try {
          // Get Promotions when one of cart date changes
          const result =
            promotions?.items?.length > 0 || promotions.fetched
              ? { type: 'GET_PROMOTIONS_SUCCESS', promotions: promotions.items }
              : await getPromotions()
          if (result.type === 'GET_PROMOTIONS_SUCCESS') {
            if (result?.promotions.length > 0) {
              result?.promotions?.map((promo) => {
                // check if this is only a rules on the dates, otherwise skip
                const checkRule = promo?.rules.find(
                  (rule) => rule.type === 'number_of_leasing_day'
                )
                if (typeof checkRule !== 'undefined' && !promo.coupon_based) {
                  // Find the right percentage of promotion based on dateRange
                  if (
                    range >= promo?.rules[0]?.configuration?.from &&
                    range <= promo?.rules[0]?.configuration?.to
                  ) {
                    promotionPercentage =
                      promo.actions.length !== 0
                        ? promo.actions[0].configuration.percentage
                        : 0
                  }
                }
              })
              // CHeck if last promotion should be applied for even more day than the range setup
              if (
                range >
                  result?.promotions[result?.promotions.length - 1].rules[0]
                    ?.configuration.to &&
                !result?.promotions[result?.promotions.length - 1].coupon_based
              ) {
                promotionPercentage =
                  result?.promotions[result?.promotions.length - 1].actions[0]
                    ?.configuration.percentage
              }
              if (isMounted) {
                if (
                  typeof promotionPercentage === 'number' &&
                  promotionPercentage !== 0
                ) {
                  setReduction(Math.round(promotionPercentage * 100))
                  setCurrentPromotion(Math.round(promotionPercentage * 100))
                  setHasReduction(true)
                } else {
                  setHasReduction(false)
                  setCurrentPromotion(null)
                }
              }
            }
          }
        } catch (error) {
          console.error('Error calculating promotion:', error)
        }
      }
      calculPromotion()
    }

    return () => {
      isMounted = false
    }
  }, [cart?.item?.startLeasingDate, cart?.item?.endLeasingDate])

  const MyContainer = ({ className, children }) => {
    const hasTextUnderCalendar = isPrismicText(text?.small_text_under_calendar)

    return (
      <div
        className={clsx([
          'datePicker',
          hasTextUnderCalendar && classes.datePickerWithText,
          calendarFade
            ? `datepicker ${classes.show}`
            : `datepicker ${classes.hide}`,
        ])}
      >
        <div className={className}>{children}</div>
        {hasTextUnderCalendar && (
          <div
            className={clsx(classes.backgroundSolid, 'datePicker__background')}
          >
            <div className={clsx(classes.datePickerText, 'datePicker__text')}>
              <CMSText
                asText
                data={text.small_text_under_calendar}
                defaultText={t('texts:general:small_text_under_calendar')}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const ButtonInput = React.forwardRef<HTMLButtonElement, any>(
    ({ value, onClick, id }, ref) => (
      <Button
        disableRipple
        size="large"
        className={`daterange__btn ${classes.mobileInput}`}
        onClick={onClick}
        ref={ref}
        data-testid={`daterange-${id}`}
      >
        {value === '' ? <Skeleton variant="text" width={'10ch'} /> : value}
      </Button>
    )
  )

  const handleDayDuration = async (event) => {
    setDayDuration(event?.target?.value)
    try {
      await patchDatesCart({
        startRentingDate: cart?.item?.startLeasingDate,
        endRentingDate: cart?.item?.endLeasingDate,
        oneDayDuration: event?.target?.value,
      })
    } catch (error) {
      console.error('Error in handleDayDuration:', error)
    }
  }

  return (
    <>
      <div
        className={clsx(
          classes.shopLayout,
          isLanding && classes.shopLayoutLanding
        )}
      >
        <form onSubmit={onSubmit} className={classes.root}>
          <div
            className={clsx(
              'daterange__container',
              enableHalfDay && classes.daterangeOneDay
            )}
          >
            <div
              className={clsx(
                'daterange',
                classes.when,
                dateLoading && classes.whenLoading,
                enableHalfDay && classes.whenOneDay
              )}
            >
              {dateLoading && (
                <>
                  <div
                    data-testid="daterange-loading"
                    className={classes.loaderOverlay}
                  >
                    <Loader />
                  </div>
                  <div className={classes.loaderSizer} />
                </>
              )}
              {isEnableChooseStartingDate && isSubscription && (
                <>
                  {CalendarIcon}
                  <DatePicker
                    locale={currentLocale}
                    dateFormat="P"
                    selected={
                      cart &&
                      cart.item &&
                      !cart?.loading &&
                      cart.item.startLeasingDate !== undefined
                        ? dayjs(
                            cart.item.startLeasingDate,
                            'DD/MM/YYYY'
                          ).toDate()
                        : null
                    }
                    placeholderText={'DD/MM/YYYY'}
                    ref={startRef}
                    filterDate={filterDate}
                    excludeDates={excludedDatesStart}
                    onChange={(date) => handleSubscriptionDayChange(date)}
                    selectsStart
                    minDate={minStartDate}
                    maxDate={getMajeRentalLastDay()?.toDate()}
                    withPortal={isMobile}
                    name={'startDate'}
                    className={classes.datepicker}
                    calendarContainer={MyContainer}
                    showPopperArrow={false}
                    popperClassName="react-datepicker-popper--no-triangle"
                    popperModifiers={{ offset: { enabled: false } }}
                    onCalendarOpen={handleOpen}
                    onCalendarClose={handleClose}
                    id="start"
                  />
                </>
              )}
              {!isEnableChooseStartingDate && !isFlex && (
                <>
                  {CalendarIcon}
                  <DatePicker
                    locale={currentLocale}
                    dateFormat="P"
                    selected={
                      cart &&
                      cart.item &&
                      !cart?.loading &&
                      cart.item.startLeasingDate !== undefined
                        ? dayjs(
                            cart.item.startLeasingDate,
                            'DD/MM/YYYY'
                          ).toDate()
                        : null
                    }
                    ref={startRef}
                    filterDate={filterDate}
                    excludeDates={excludedDatesStart}
                    onChange={(date) => handleStartDayChange(date)}
                    selectsStart
                    customInput={
                      <ButtonInput
                        value={cart?.item?.startLeasingDate}
                        onClick
                      />
                    }
                    startDate={
                      cart &&
                      cart.item &&
                      cart.item.startLeasingDate !== undefined &&
                      dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY').toDate()
                    }
                    minDate={minStartDate}
                    maxDate={getMajeRentalLastDay()?.toDate()}
                    withPortal={isMobile}
                    name={'startDate'}
                    onChangeRaw={handleDateChangeRaw}
                    endDate={
                      cart &&
                      cart.item &&
                      cart.item.endLeasingDate !== undefined &&
                      dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').toDate()
                    }
                    className={classes.datepicker}
                    calendarContainer={MyContainer}
                    showPopperArrow={false}
                    popperClassName="react-datepicker-popper--no-triangle"
                    popperModifiers={{ offset: { enabled: false } }}
                    onCalendarOpen={handleOpen}
                    onCalendarClose={handleClose}
                    id="start"
                  />
                  {hasReduction && cart.loading !== true ? (
                    <MainTooltip
                      placement={isDesktop ? 'top' : 'bottom'}
                      PopperProps={{
                        disablePortal: true,
                        className: clsx(
                          'daterange_popper',
                          'MuiTooltip-popper',
                          'MuiTooltip-popperArrow'
                        ),
                      }}
                      open={displayTooltip}
                      title={`${prismicText(
                        text?.you_save,
                        t('texts:general:you_save')
                      )} ${reduction.toFixed(0)} %`}
                      arrow
                      enterDelay={2000}
                    >
                      <ArrowRightIcon
                        className={clsx(
                          'daterange__icon',
                          'daterange__icon--arrow',
                          classes.iconArrow
                        )}
                      />
                    </MainTooltip>
                  ) : (
                    <ArrowRightIcon
                      className={clsx(
                        'daterange__icon',
                        'daterange__icon--arrow',
                        classes.iconArrow
                      )}
                    />
                  )}
                  <DatePicker
                    locale={currentLocale}
                    dateFormat="P"
                    selected={
                      cart &&
                      cart.item &&
                      !cart?.loading &&
                      cart.item.endLeasingDate !== undefined
                        ? dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').toDate()
                        : null
                    }
                    ref={endRef}
                    onChange={(date) => handleEndDayChange(date)}
                    onChangeRaw={handleDateChangeRaw}
                    selectsEnd
                    name={'endDate'}
                    withPortal={isMobile}
                    customInput={
                      <ButtonInput value={cart?.item?.endLeasingDate} onClick />
                    }
                    startDate={
                      cart &&
                      cart.item &&
                      cart.item.startLeasingDate !== undefined &&
                      dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY').toDate()
                    }
                    endDate={
                      cart &&
                      cart.item &&
                      cart.item.startLeasingDate !== undefined
                        ? dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').toDate()
                        : minEndDate
                    }
                    minDate={minEndDate}
                    maxDate={getRentalEndMaxDate(
                      cart?.item?.startLeasingDate,
                      parameters?.items?.maximumLeasingDays
                    )}
                    openToDate={
                      cart &&
                      cart.item &&
                      cart.item.startLeasingDate !== undefined
                        ? dayjs(
                            cart.item.startLeasingDate,
                            'DD/MM/YYYY'
                          ).toDate()
                        : minStartDate
                    }
                    className={classes.datepicker}
                    calendarContainer={MyContainer}
                    filterDate={filterDate}
                    excludeDates={excludedDatesEnd}
                    showPopperArrow={false}
                    popperClassName="react-datepicker-popper--no-triangle"
                    popperModifiers={{ offset: { enabled: false } }}
                    onCalendarOpen={handleOpen}
                    onCalendarClose={handleClose}
                    id="end"
                  />
                </>
              )}
            </div>
            {enableHalfDay &&
              cart?.item?.startLeasingDate === cart?.item?.endLeasingDate && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <select
                    value={dayDuration}
                    onChange={handleDayDuration}
                    className={clsx(
                      'contact_select',
                      classes.dayDurationSelctor
                    )}
                  >
                    {dayDurationValues.map((option, index) => {
                      return (
                        <option key={index} value={option.value}>
                          {prismicText(option?.text, option.defaultText)}
                        </option>
                      )
                    })}
                  </select>
                </FormControl>
              )}
          </div>
          <div className={classes.order2}>
            <Button
              disabled={
                !cart?.item?.startLeasingDate ||
                !cart?.item?.endLeasingDate ||
                cart.loading
              }
              data-testid="submitDates"
              variant="contained"
              color="secondary"
              size="large"
              type="submit"
              onClick={onSubmit}
              className={clsx('daterange__submit', classes.btnSubmit)}
            >
              {isLanding ? (
                <CMSText
                  asText
                  data={text?.start_button}
                  defaultText={t('texts:general:start_button')}
                />
              ) : (
                <CMSText
                  asText
                  data={text?.confirm_button}
                  defaultText={t('texts:general:confirm_button')}
                />
              )}
            </Button>
          </div>
          <div className={classes.order1}>
            {!isLanding && (
              <ShippingInfos
                startDate={cart?.item?.startLeasingDate}
                endDate={cart?.item?.endLeasingDate}
                deliverDuration={parameters?.items?.deliverDuration}
                returnDuration={parameters?.items?.returnDuration}
                loading={parameters?.loading}
                t={t}
                text={textFunnel}
                isEnableChooseStartingDate={isEnableChooseStartingDate}
              />
            )}
          </div>
        </form>
      </div>
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(DateRange)
